import type { FlightFilterOptions, FlightFilterState, StopoverPresence } from './flightFilters';
import {
  dayIndex,
  isoFromDayIndex,
  formatDayMonth,
  defaultFilterState,
  NIGHTS_DOMAIN_MAX,
  NIGHTS_DOMAIN_MIN,
} from './flightFilters';
import { DualRangeSlider } from '../DualRangeSlider';
import { CarrierFilterChips } from './CarrierFilterChips';
import { StopoverPresenceToggle } from './StopoverPresenceToggle';

export interface FlightFilterBarProps {
  options: FlightFilterOptions;
  value: FlightFilterState;
  onChange: (value: FlightFilterState) => void;
  matched: number;
  total: number;
}

const KNOWN_LEGS = ['outbound', 'inbound'] as const;

export function FlightFilterBar({ options, value, onChange, matched, total }: FlightFilterBarProps) {
  const defaults = defaultFilterState(options);
  const isDefault =
    rangesEqual(value.outbound, defaults.outbound) &&
    rangesEqual(value.inbound, defaults.inbound) &&
    rangesEqual(value.nights, defaults.nights) &&
    value.leg === defaults.leg &&
    value.carriers.length === 0 &&
    value.stopover === defaults.stopover;

  function setOutbound(next: [number, number]) {
    if (!options.outbound) return;
    onChange({
      ...value,
      outbound: [isoFromDayIndex(next[0], options.outbound.min), isoFromDayIndex(next[1], options.outbound.min)],
    });
  }

  function setInbound(next: [number, number]) {
    if (!options.inbound) return;
    onChange({
      ...value,
      inbound: [isoFromDayIndex(next[0], options.inbound.min), isoFromDayIndex(next[1], options.inbound.min)],
    });
  }

  function setNights(next: [number, number]) {
    onChange({ ...value, nights: [next[0], next[1]] });
  }

  function setLeg(next: string | null) {
    onChange({ ...value, leg: next });
  }

  function setStopover(next: StopoverPresence) {
    onChange({ ...value, stopover: next });
  }

  function setCarriers(next: string[]) {
    onChange({ ...value, carriers: next });
  }

  const missingLegs = KNOWN_LEGS.filter((leg) => !options.stopover_legs.includes(leg));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {options.outbound && value.outbound && (
          <div>
            <DualRangeSlider
              label="Outbound"
              min={0}
              max={dayIndex(options.outbound.max, options.outbound.min)}
              value={[
                dayIndex(value.outbound[0], options.outbound.min),
                dayIndex(value.outbound[1], options.outbound.min),
              ]}
              onChange={setOutbound}
              // DualRangeSlider hands formatValue its NUMERIC position, but
              // formatDayMonth expects an ISO date. Passing it bare made the
              // slider call "2".split('-') on first render, which threw and
              // took the whole page down to a blank screen.
              formatValue={(index) => formatDayMonth(isoFromDayIndex(index, options.outbound!.min))}
            />
            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
              <span>{formatDayMonth(options.outbound.min)}</span>
              <span>{formatDayMonth(options.outbound.max)}</span>
            </div>
          </div>
        )}

        {options.inbound && value.inbound && (
          <div>
            <DualRangeSlider
              label="Inbound"
              min={0}
              max={dayIndex(options.inbound.max, options.inbound.min)}
              value={[
                dayIndex(value.inbound[0], options.inbound.min),
                dayIndex(value.inbound[1], options.inbound.min),
              ]}
              onChange={setInbound}
              formatValue={(index) => formatDayMonth(isoFromDayIndex(index, options.inbound!.min))}
            />
            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
              <span>{formatDayMonth(options.inbound.min)}</span>
              <span>{formatDayMonth(options.inbound.max)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <StopoverPresenceToggle value={value.stopover} onChange={setStopover} />

        {value.nights && (
          <div className="w-48">
            <DualRangeSlider
              label="Stopover nights"
              min={NIGHTS_DOMAIN_MIN}
              max={NIGHTS_DOMAIN_MAX}
              value={value.nights}
              onChange={setNights}
              disabled={value.stopover === 'direct'}
            />
            <div className="text-[10px] text-gray-500 mt-0.5">
              {options.stopover_nights
                ? options.stopover_nights.values.length === 1
                  ? `Observed: ${options.stopover_nights.values[0]}`
                  : `Observed: ${options.stopover_nights.min}–${options.stopover_nights.max}`
                : 'No stopovers observed yet'}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor="stopover-leg" className="text-xs font-medium text-gray-700">
            Stopover leg
          </label>
          <select
            id="stopover-leg"
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            value={value.leg ?? ''}
            onChange={(e) => setLeg(e.target.value || null)}
            disabled={value.stopover === 'direct'}
          >
            <option value="">All legs</option>
            {KNOWN_LEGS.map((leg) => {
              const hasData = options.stopover_legs.includes(leg);
              return (
                <option key={leg} value={leg} disabled={!hasData}>
                  {hasData ? leg : `${leg} — no data collected`}
                </option>
              );
            })}
          </select>
          {missingLegs.length > 0 && (
            <p className="text-[10px] text-amber-700">
              {missingLegs.map((leg) => `${leg} is configured upstream but has never been returned`).join('; ')}
            </p>
          )}
        </div>

        <CarrierFilterChips carriers={options.carriers} value={value.carriers} onChange={setCarriers} />

        <div className="flex-1" />

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            Showing {matched} of {total} itineraries
          </span>
          <button
            type="button"
            onClick={() => onChange(defaults)}
            disabled={isDefault}
            className="px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}

function rangesEqual<T>(a: [T, T] | null, b: [T, T] | null): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a[0] === b[0] && a[1] === b[1];
}
